import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import '../../styles/components/_codeEditor.scss';
import {
    Box,
    FormControl,
    FormHelperText,
    FormLabel,
    Dialog,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Slide,
    Button
} from '@mui/material';
import {
    Fullscreen as FullscreenIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { safeStringifyJSON, validateAndParseJson } from '../../utils/jsonValidator';

// Animación de entrada para el modal
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export const MultiLanguageEditor = ({
    value,
    onChange,
    dfLanguage = 'json',
    htCtrl = '400',
    errorInputs = {},
    field
}) => {
    const editorRef = useRef(null);
    const [editorValue, setEditorValue] = useState(() =>
        dfLanguage === 'json' ? safeStringifyJSON(value) : value
    );
    const [isValid, setIsValid] = useState(true);
    const [isFocused, setIsFocused] = useState(false);
    const [openFullscreen, setOpenFullscreen] = useState(false);

    const handleEditorChange = (val) => {
        setEditorValue(val);
        if (dfLanguage === 'json') {
            const { valid, json } = validateAndParseJson(val);
            if (valid) {
                onChange(json);
                setIsValid(true);
            } else {
                setIsValid(false);
            }
        } else {
            onChange(val);
            setIsValid(true);
        }
    };

    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        editor.onDidFocusEditorWidget(() => setIsFocused(true));
        editor.onDidBlurEditorWidget(() => setIsFocused(false));
    };

    useEffect(() => {
        setEditorValue(dfLanguage === 'json' ? safeStringifyJSON(value) : value);
    }, [value, dfLanguage]);

    const handleToggleFullscreen = () => setOpenFullscreen(!openFullscreen);

    const renderEditor = (height) => (
        <Editor
            height={height}
            defaultLanguage={dfLanguage}
            value={editorValue}
            onChange={handleEditorChange}
            onMount={handleEditorDidMount}
            options={{
                wordWrap: "on",
                scrollBeyondLastLine: false,
                minimap: { enabled: false },
                automaticLayout: true,
                fontSize: openFullscreen ? 16 : 14,
            }}
        />
    );

    return (
        <FormControl fullWidth error={Boolean(errorInputs[field?.id]?.isError)}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <FormLabel sx={{ mb: 0 }}>
                    {field?.label} {field?.required && " *"}
                </FormLabel>
                <FullscreenIcon
                    onClick={handleToggleFullscreen}
                    sx={{
                        height: 25,
                        cursor: "pointer",
                        color: '#6728AF'
                    }}
                />
            </Box>
            <Box
                sx={{
                    border: "1px solid",
                    borderColor: errorInputs[field?.id]?.isError ? "red" : isValid ? "divider" : "red",
                    borderRadius: 1,
                    overflow: "hidden",
                    height: `${htCtrl}px`,
                    position: 'relative'
                }}
                className={`editor-container ${!isValid ? "invalid" : isFocused ? "focused" : ""}`}
            >
                {renderEditor("100%")}
            </Box>

            <Dialog
                fullScreen
                open={openFullscreen}
                onClose={handleToggleFullscreen}
                TransitionComponent={Transition}
            >
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            {field?.label || 'Editor:'}
                        </Typography>
                        <IconButton edge="start" color="inherit" onClick={handleToggleFullscreen}>
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Box sx={{ flexGrow: 1, height: 'calc(100vh - 64px)' }}>
                    {renderEditor("100%")}
                </Box>
            </Dialog>

            {errorInputs[field?.id]?.isError && (
                <FormHelperText>{errorInputs[field?.id]?.messageError}</FormHelperText>
            )}
        </FormControl>
    );
};
